// Объявления Telegram WebApp API
declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  section_bg_color?: string;
  section_separator_color?: string;
  subtitle_text_color?: string;
  accent_text_color?: string;
  destructive_text_color?: string;
  header_bg_color?: string;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    start_param?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  BackButton: {
    isVisible: boolean;
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  MainButton: any;
  HapticFeedback: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  ready(): void;
  expand(): void;
  close(): void;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
  enableClosingConfirmation(): void;
  disableClosingConfirmation(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  disableVerticalSwipes(): void;
}

export const getTelegramWebApp = (): TelegramWebApp | undefined => {
  return typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
};

const INIT_DATA_STORAGE_KEY = 'runhelper_telegram_init_data';

const getLaunchParameter = (name: string): string => {
  if (typeof window === 'undefined') return '';
  const sources = [
    new URLSearchParams(window.location.search),
    new URLSearchParams(window.location.hash.replace(/^#/, ''))
  ];
  for (const source of sources) {
    const value = source.get(name);
    if (value) return value;
  }
  return '';
};

export const getTelegramInitData = (): string => {
  if (typeof window === 'undefined') return '';
  const current = getTelegramWebApp()?.initData
    || getLaunchParameter('tgWebAppData')
    || '';
  if (current) {
    window.sessionStorage.setItem(INIT_DATA_STORAGE_KEY, current);
    return current;
  }
  return window.sessionStorage.getItem(INIT_DATA_STORAGE_KEY) || '';
};

const getUserFromInitData = () => {
  try {
    const rawUser = new URLSearchParams(getTelegramInitData()).get('user');
    return rawUser ? JSON.parse(rawUser) as InitResult['user'] : undefined;
  } catch {
    return undefined;
  }
};

// Применяем тему Telegram как CSS-переменные на :root
const applyTheme = (tg: TelegramWebApp) => {
  const t = tg.themeParams;
  const root = document.documentElement;
  const set = (name: string, value: string | undefined, fallback: string) => {
    root.style.setProperty(name, value || fallback);
  };

  set('--tg-theme-bg-color', t.bg_color, '#ffffff');
  set('--tg-theme-text-color', t.text_color, '#000000');
  set('--tg-theme-hint-color', t.hint_color, '#999999');
  set('--tg-theme-link-color', t.link_color, '#3390ec');
  set('--tg-theme-button-color', t.button_color, '#3390ec');
  set('--tg-theme-button-text-color', t.button_text_color, '#ffffff');
  set('--tg-theme-secondary-bg-color', t.secondary_bg_color, '#f0f0f0');
  set('--tg-theme-section-bg-color', t.section_bg_color ?? t.bg_color, '#ffffff');
  set('--tg-theme-subtitle-text-color', t.subtitle_text_color ?? t.hint_color, '#999999');
  set('--tg-theme-accent-text-color', t.accent_text_color ?? t.link_color, '#3390ec');
  set('--tg-theme-destructive-text-color', t.destructive_text_color, '#ff3b30');
  set('--tg-theme-section-separator-color', t.section_separator_color, '#e5e5ea');

  root.setAttribute('data-theme', tg.colorScheme || 'light');
};

export const haptic = {
  light: () => getTelegramWebApp()?.HapticFeedback?.impactOccurred('light'),
  medium: () => getTelegramWebApp()?.HapticFeedback?.impactOccurred('medium'),
  heavy: () => getTelegramWebApp()?.HapticFeedback?.impactOccurred('heavy'),
  soft: () => getTelegramWebApp()?.HapticFeedback?.impactOccurred('soft'),
  success: () => getTelegramWebApp()?.HapticFeedback?.notificationOccurred('success'),
  warning: () => getTelegramWebApp()?.HapticFeedback?.notificationOccurred('warning'),
  error: () => getTelegramWebApp()?.HapticFeedback?.notificationOccurred('error'),
  selection: () => getTelegramWebApp()?.HapticFeedback?.selectionChanged()
};

export interface InitResult {
  telegramId?: number;
  user?: {
    id: number;
    first_name?: string;
    last_name?: string;
    username?: string;
  };
  isTestMode: boolean;
}

// Инициализация Telegram WebApp: ready, expand, тема, closing confirmation
export const initTelegramWebApp = (): InitResult => {
  const tg = getTelegramWebApp();
  const launchUser = getUserFromInitData();

  if (!tg) {
    return launchUser?.id
      ? { telegramId: launchUser.id, user: launchUser, isTestMode: false }
      : { isTestMode: true };
  }

  try {
    tg.ready();
    tg.expand();
    tg.disableVerticalSwipes?.();
    tg.enableClosingConfirmation?.();
    applyTheme(tg);

    tg.onEvent('themeChanged', () => applyTheme(tg));

    const user = tg.initDataUnsafe?.user || launchUser;
    const telegramId = user?.id;

    if (!telegramId) {
      return { isTestMode: true };
    }

    return { telegramId, user, isTestMode: false };
  } catch (e) {
    console.error('Telegram WebApp init error:', e);
    return { isTestMode: true };
  }
};

export {};
